import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { type FileRejection, useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EXPERIENCES_COORDINATOR } from '~/consts/roles';

import {
  ACCEPTED_IMAGE_DROPZONE_TYPES,
  MAX_IMAGE_SIZE,
  useGetAttractionImages,
  useResetAttractionImage,
  useUpdateAttractionImage,
  useUploadAttractionImage,
} from '~/hooks/apis/attractions';

interface Params {
  attractionId: string;
}

function AttractionImageEditor() {
  const { attractionId } = useParams<keyof Params & string>();
  const { data: imagesData, isLoading, error } = useGetAttractionImages(attractionId);
  const images = imagesData?.result;
  const updateMutation = useUpdateAttractionImage();
  const resetMutation = useResetAttractionImage();
  const uploadMutation = useUploadAttractionImage();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedImage, setSelectedImage] = useState<{ imageId: string; url: string } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      fileRejections.forEach(({ file, errors }) => {
        enqueueSnackbar(`"${file.name}" ${errors.map((e) => e.message).join(' and ')}`, { variant: 'error' });
      });
      const file = acceptedFiles[0];
      if (file) {
        uploadMutation.mutate({ attractionId, file });
      }
    },
    [attractionId, uploadMutation, enqueueSnackbar],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_IMAGE_DROPZONE_TYPES,
    maxFiles: 1,
    maxSize: MAX_IMAGE_SIZE,
    disabled: uploadMutation.isPending,
  });

  const handleSelect = (imageId: string, url: string) => {
    setSelectedImage({ imageId, url });
    setConfirmOpen(true);
  };

  const handleConfirmUpdate = () => {
    if (!selectedImage) {
      return;
    }
    updateMutation.mutate(
      { attractionId, imageId: selectedImage.imageId, imageUrl: selectedImage.url },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          setSelectedImage(null);
        },
      },
    );
  };

  const handleConfirmReset = () => {
    resetMutation.mutate(attractionId, {
      onSuccess: () => setConfirmReset(false),
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load available images for attraction {attractionId}</Alert>;
  }

  const uniqueImages = images ? Array.from(new Map(images.map((img) => [img.imageId, img])).values()) : [];

  return (
    <Container>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} mt={3}>
        <Typography variant="h5">Attraction Image Editor</Typography>
        <Button
          variant="outlined"
          startIcon={<RestartAltIcon />}
          onClick={() => setConfirmReset(true)}
          disabled={resetMutation.isPending}
        >
          Reset to Auto
        </Button>
      </Stack>

      <Typography variant="subtitle1" color="text.secondary" mb={2}>
        Attraction: {attractionId}
      </Typography>

      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.400',
          borderRadius: 2,
          p: 3,
          mb: 3,
          textAlign: 'center',
          cursor: uploadMutation.isPending ? 'wait' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'transparent',
          transition: 'all 0.2s',
        }}
      >
        <input {...getInputProps()} />
        {uploadMutation.isPending ? (
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
            <CircularProgress size={20} />
            <Typography>Uploading...</Typography>
          </Stack>
        ) : (
          <Stack spacing={1} alignItems="center">
            <CloudUploadIcon sx={{ fontSize: 40, color: 'grey.500' }} />
            <Typography variant="body1">{isDragActive ? 'Drop image here' : 'Upload custom image'}</Typography>
            <Typography variant="caption" color="text.secondary">
              Drag and drop or click to select. Accepted: JPEG, PNG, WebP (max 10MB)
            </Typography>
          </Stack>
        )}
      </Box>

      {uniqueImages.length === 0 ? (
        <Alert severity="info">No images available from linked offers.</Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Or select an image from linked offers. ({uniqueImages.length} available)
          </Typography>
          <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))" gap={2}>
            {uniqueImages.map((img) => (
              <Card
                key={img.imageId}
                sx={{
                  cursor: 'pointer',
                  border: 2,
                  borderColor: selectedImage?.imageId === img.imageId ? 'primary.main' : 'transparent',
                  '&:hover': { borderColor: 'primary.light' },
                }}
                onClick={() => handleSelect(img.imageId, img.url)}
              >
                <CardMedia
                  component="img"
                  height="180"
                  image={img.url}
                  alt={img.offerTitle}
                  sx={{ objectFit: 'cover' }}
                />
                <Box p={1.5}>
                  <Typography variant="body2" noWrap title={img.offerTitle}>
                    {img.offerTitle}
                  </Typography>
                  <Stack direction="row" spacing={1} mt={0.5}>
                    {img.isHero && <Chip label="Hero" size="small" color="primary" variant="outlined" />}
                    <Typography variant="caption" color="text.secondary">
                      {img.imageId.length > 20 ? `${img.imageId.slice(0, 20)}...` : img.imageId}
                    </Typography>
                  </Stack>
                </Box>
              </Card>
            ))}
          </Box>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Image Change</DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box>
              <Typography mb={2}>Set this image as the hero image for attraction {attractionId}?</Typography>
              <img src={selectedImage.url} alt="Selected" style={{ width: '100%', borderRadius: 8 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmUpdate}
            disabled={updateMutation.isPending}
            startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)} maxWidth="xs">
        <DialogTitle>Reset to Auto</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the manual image override and restore automatic image selection from the highest-ranked
            linked offer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReset(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmReset}
            disabled={resetMutation.isPending}
            startIcon={resetMutation.isPending ? <CircularProgress size={16} /> : <RestartAltIcon />}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default function PermissionedAttractionImageEditor() {
  return (
    <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EXPERIENCES_COORDINATOR]}>
      <AttractionImageEditor />
    </PermissionedComponent>
  );
}
